<script setup lang="ts">
import type { Room } from '~~/shared/types/payload-types'

type FloorValue = 'souterrain' | 'ground' | 'first' | 'second' | 'third' | 'fourth' | 'garden'

export interface Props {
  floor: FloorValue
  rooms: Room[]
  includeLabel?: boolean
  selectedSlug?: string
}

const props = withDefaults(defineProps<Props>(), {
  includeLabel: true,
})

const router = useRouter()
const localePath = useLocalePath()
const containerRef = ref<HTMLDivElement | null>(null)
const myBbox = ref<{ x: number; y: number; width: number; height: number } | null>(null)
const { report, sharedX, sharedWidth } = useFloorBounds()

const FLOOR_NAME_TO_PREFIX: Record<string, string> = {
  souterrain: 'floor-01', ground: 'floor-02', first: 'floor-03', second: 'floor-04', third: 'floor-05', fourth: 'floor-06', garden: 'floor-07',
}

// "souterrain-007" → "floor-01-007" (matches SVG element IDs)
function toSvgId(slug: string): string {
  const m = slug.match(/^([a-z]+)-(\d+)$/)
  if (!m) return slug
  const prefix = FLOOR_NAME_TO_PREFIX[m[1]!]
  return prefix ? `${prefix}-${m[2]}` : slug
}

type RoomEntry = { slug: string; hasContent: boolean }

// Map SVG element ID → room entry for every room on this floor
const roomMap = computed(() => {
  const map = new Map<string, RoomEntry>()
  for (const room of props.rooms) {
    const filename = (room as any).maskShape?.filename as string | undefined
    if (!filename) continue
    map.set(toSvgId(filename.replace('.svg', '')), {
      slug: room.slug,
      hasContent: Boolean((room as any).hasContent),
    })
  }
  return map
})

const roomStats = computed(() => {
  const withContent = props.rooms.filter(room => Boolean((room as any).hasContent)).length
  return { total: props.rooms.length, withContent }
})

// cached per visit in useState and preloaded on idle, so revisits render instantly
const { svgs, load: loadFloorSvg } = useFloorSvgs()
const rawSvg = computed(() => svgs.value[props.floor] ?? '')

watch(() => props.floor, floor => loadFloorSvg(floor), { immediate: import.meta.client })

// Pre-process SVG: inject class, fill, stroke (hairline fix), and data-room on every shape.
const processedSvg = computed(() => {
  const svg = rawSvg.value
  if (!svg) return ''


  return svg.replace(
    /id="(floor-\d{2}-\d+)"/g,
    (_: string, id: string) => {
      
      const entry = roomMap.value.get(id)
      const fill = !entry
        ? '#ebebeb'
        : (props.selectedSlug && entry.slug === props.selectedSlug)
          ? 'var(--color--accent, #b0b0b0)'
          : !entry.hasContent
            ? '#d5d5d5'
            : '#8e8e8e'
      const isSelected = !!(props.selectedSlug && entry?.slug === props.selectedSlug)
      const extra = !entry
        ? ''
        : isSelected || entry.hasContent
          ? ` data-room="${entry.slug}"`
          : ` data-room="${entry.slug}" data-idle="true"`
      // stroke matches fill to close sub-pixel gaps between adjacent shapes
      return `id="${id}" class="room-shape"${extra} fill="${fill}" stroke="${fill}" stroke-width=".1" paint-order="stroke fill"`
    },
  )
})

async function measureBbox() {
  await nextTick()
  const svg = containerRef.value?.querySelector('svg')
  if (!svg) return
  // getBBox on the root <svg> is unreliable (may return the viewBox, not content bounds).
  // Compute the union of all child shape bboxes instead.
  const shapes = svg.querySelectorAll<SVGGraphicsElement>('path, rect, polygon, circle, ellipse, polyline')
  if (!shapes.length) return
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
  for (const el of shapes) {
    const b = el.getBBox()
    x0 = Math.min(x0, b.x);       y0 = Math.min(y0, b.y)
    x1 = Math.max(x1, b.x + b.width); y1 = Math.max(y1, b.y + b.height)
  }
  if (x1 === -Infinity) return
  myBbox.value = { x: x0, y: y0, width: x1 - x0, height: y1 - y0 }
  report(x0, x1)
}

// onMounted handles hard load / SSR hydration (processedSvg already set, watch won't fire).
// watch handles client-side fetch completing after mount.
onMounted(measureBbox)
watch(processedSvg, measureBbox)

// Full building horizontal bounds (union of all 7 floors' rect content).
// Used as a minimum so a single-floor view (e.g. slug page) stays at the same scale.
const BUILDING_X = 79.62
const BUILDING_RIGHT = 1508.42

// Re-apply viewBox whenever this floor's bbox or the shared horizontal bounds change.
watch([myBbox, sharedX, sharedWidth], () => {
  const svg = containerRef.value?.querySelector('svg')
  if (!svg || !myBbox.value || sharedX.value === null || sharedWidth.value === null) return
  const vbX = Math.min(sharedX.value, BUILDING_X)
  const vbRight = Math.max(sharedX.value + sharedWidth.value, BUILDING_RIGHT)
  svg.setAttribute(
    'viewBox',
    `${vbX} ${myBbox.value.y} ${vbRight - vbX} ${myBbox.value.height}`,
  )
})

function handleClick(e: MouseEvent) {
  const target = (e.target as Element).closest<SVGElement>('[data-room]')
  if (!target || target.dataset.idle) return
  const slug = target.dataset.room
  if (slug) router.push(localePath(`/rooms/${slug}`))
}
</script>

<template>
  <div>
    <h3
      v-if="includeLabel"
      class="text-md">
      {{ $t(`floorplan.floors.${floor}`) }}
    </h3>

    <div class="text-sm mb-2 mt-0_1">
      {{ $t('floorplan.roomStats', { count: roomStats.total, total: roomStats.total, withContent: roomStats.withContent }) }}
    </div>

    <div
      class="[&_svg]:block [&_svg]:w-full [&_svg]:h-auto [&_[data-room]:not([data-idle])]:cursor-pointer [&_[data-room]:not([data-idle]):hover]:fill-accent [&_[data-room]:not([data-idle]):hover]:stroke-accent"
      ref="containerRef"
      v-html="processedSvg"
      @click="handleClick"
    />
  </div>
</template>
